import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Nested Donut Graph (amCharts style): same data and categoryField for both rings,
 * inner ring = first measure (valueField), outer ring = second measure (valueField).
 */
@Component({
  selector: 'bi-nested-donut-graph',
  standalone: false,
  templateUrl: './nested-donut-graph.component.html',
  styleUrl: './nested-donut-graph.component.scss'
})
export class NestedDonutGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private subscription: any;
  private viewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.subscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data?.reportData?.length) {
          this.reportData = data;
          this.data = this._baseChartService.transformReportData(data);
          if (this.viewInitialized) this.updateChart(this.data);
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.reportData?.reportData?.length) {
      this.zone.runOutsideAngular(() => {
        const el = document.getElementById('nested-donut-chartdiv');
        if (el) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        }
      });
    }
  }

  private createChart(data: BarChartData[]): void {
    if (this.isCreatingChart) return;
    this.isCreatingChart = true;

    const container = document.getElementById('nested-donut-chartdiv');
    if (!container) {
      this.isCreatingChart = false;
      return;
    }

    if (this.root) {
      try {
        this._baseChartService.disposeChart(this.root);
      } catch (_) {}
      this.root = undefined as any;
    }

    if (container.children.length) container.innerHTML = '';

    try {
      this.root = this._baseChartService.createRoot('nested-donut-chartdiv');
      this._baseChartService.attachExporting(this.root, { filePrefix: 'nested-donut', data });

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);
      const innerValueField = valueFields[0];
      const outerValueField = valueFields.length > 1 ? valueFields[1] : valueFields[0];
      const chartData = data as BarChartData[];

      const chart = this.root.container.children.push(am5percent.PieChart.new(this.root, {
        layout: this.root.verticalLayout,
        innerRadius: am5.percent(40),
        radius: am5.percent(100)
      }));

      this._baseChartService.setupChartAnimation(chart, 'elastic');
      const bgColor = this.root.interfaceColors.get('background');

      const series0 = chart.series.push(am5percent.PieSeries.new(this.root, {
        valueField: innerValueField,
        categoryField,
        alignLabels: true,
        innerRadius: am5.percent(15),
        radius: am5.percent(45)
      }));
      series0.ticks.template.setAll({ forceHidden: true });
      series0.labels.template.setAll({ forceHidden: true });
      series0.slices.template.setAll({
        stroke: bgColor,
        strokeWidth: 2,
        tooltipText: "{category}: {valuePercentTotal.formatNumber('0.00')}% ({value})"
      });
      series0.slices.template.states.create('hover', { scale: 0.95 });

      const series1 = chart.series.push(am5percent.PieSeries.new(this.root, {
        valueField: outerValueField,
        categoryField,
        alignLabels: true,
        innerRadius: am5.percent(45),
        radius: am5.percent(100)
      }));
      series1.ticks.template.setAll({ forceHidden: true });
      series1.labels.template.setAll({ forceHidden: true });
      series1.slices.template.setAll({
        stroke: bgColor,
        strokeWidth: 2,
        tooltipText: "{category}: {valuePercentTotal.formatNumber('0.00')}% ({value})"
      });
      series1.slices.template.states.create('hover', { scale: 0.95 });

      this._baseChartService.setupSeriesAnimation(series0, 'bounce');
      this._baseChartService.setupSeriesAnimation(series1, 'bounce');

      series0.data.setAll(chartData);
      series1.data.setAll(chartData);

      const legend = chart.children.push(this._baseChartService.createLegend(this.root));
      legend.markers.template.set('width', 12);
      legend.markers.template.set('height', 12);
      legend.data.setAll([...series0.dataItems, ...series1.dataItems]);
      this._baseChartService.setupLegendAnimation(legend);

      series0.appear(1000, 100);
      series1.appear(1000, 100);

      BaseChartService.updateActiveChartData(data);
    } catch (err) {
      if (this.root) {
        try { this._baseChartService.disposeChart(this.root); } catch (_) {}
        this.root = undefined as any;
      }
    } finally {
      this.isCreatingChart = false;
    }
  }

  private updateChart(data: BarChartData[]): void {
    if (this.isCreatingChart) return;
    const container = document.getElementById('nested-donut-chartdiv');
    if (!container) {
      setTimeout(() => this.updateChart(data), 100);
      return;
    }
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    setTimeout(() => this.createChart(data), 200);
  }

  ngOnDestroy(): void {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    this.subscription?.unsubscribe();
    this.isCreatingChart = false;
    this.viewInitialized = false;
  }
}
